import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/th';
import { useTranslation } from 'react-i18next';
import { SERVICE_URL } from '../../utils/api';
import { numberFormat } from '../../utils/functions';
import ChangeLanguageDropdown from '../common/ChangeLanguageDropdown';

// หน้าลูกค้ากรอกบัญชีรับเงินประกันคืน (ลิงก์ใน SMS) /once/deposit/:token
// ใช้ axios ตรง ไม่ผ่าน api helper -- endpoint ไม่ต้องใช้ session และลูกค้าที่เปิดจาก SMS อาจยังไม่มี session
const BANKS = [
  'ธนาคารกสิกรไทย', 'ธนาคารไทยพาณิชย์', 'ธนาคารกรุงเทพ', 'ธนาคารกรุงไทย', 'ธนาคารกรุงศรีอยุธยา',
  'ธนาคารทหารไทยธนชาต (ttb)', 'ธนาคารออมสิน', 'ธนาคารเพื่อการเกษตรและสหกรณ์การเกษตร (ธ.ก.ส.)',
  'ธนาคารอาคารสงเคราะห์', 'ธนาคารยูโอบี', 'ธนาคารซีไอเอ็มบี ไทย', 'ธนาคารเกียรตินาคินภัทร', 'ธนาคารแลนด์ แอนด์ เฮ้าส์',
];
const OTHER = '__other__';

const req = (method, path, data) => axios({ baseURL: SERVICE_URL, url: `deposit-public/${path}`, method, data, timeout: 30000 });

export default function DepositRefund() {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'th';
  const [info, setInfo] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [editing, setEditing] = useState(false);
  const [bank, setBank] = useState('');
  const [bankOther, setBankOther] = useState('');
  const [accountNo, setAccountNo] = useState('');
  const [accountName, setAccountName] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => { moment.locale(lang === 'th' ? 'th' : 'en'); }, [lang]);
  useEffect(() => {
    req('get', encodeURIComponent(token)).then((r) => setInfo(r.data))
      .catch((e) => { if ([404, 410].includes(e?.response?.status)) setInvalid(true); else setLoadError(true); });
  }, [token]);

  const fmtDate = (d) => moment(d).format('D MMM YYYY');
  const baht = (n) => `${numberFormat(n, true)} ${t('currency_unit')}`;

  const startEdit = () => {
    const known = info?.bank?.bank_name;
    if (known && !BANKS.includes(known)) { setBank(OTHER); setBankOther(known); } else { setBank(known || ''); setBankOther(''); }
    setAccountNo(''); setAccountName(info?.bank?.account_name || ''); setFormError(null); setSaved(false); setEditing(true);
  };

  const submit = (e) => {
    e.preventDefault();
    const bankName = (bank === OTHER ? bankOther : bank).trim();
    const acc = accountNo.replace(/[^0-9]/g, '');
    if (!bankName) { setFormError(t('dr_err_bank')); return; }
    if (acc.length < 10 || acc.length > 15) { setFormError(t('dr_err_account_no')); return; }
    if (!accountName.trim()) { setFormError(t('dr_err_account_name')); return; }
    setBusy(true); setFormError(null);
    req('put', `${encodeURIComponent(token)}/bank`, { bank_name: bankName, account_no: acc, account_name: accountName.trim() })
      .then((r) => { setInfo(r.data); setEditing(false); setSaved(true); setBusy(false); })
      .catch((err) => { setBusy(false); setFormError(err?.response?.data?.message || t('dr_err_generic')); });
  };

  const wrap = (children) => (
    <div className="container py-4" style={{ maxWidth: 520 }}>
      <div className="text-end mb-2"><ChangeLanguageDropdown /></div>
      {children}
    </div>
  );

  if (invalid) return wrap(<div className="text-center p-4"><h4>🔒</h4><p>{t('dr_invalid_link')}</p></div>);
  if (loadError) return wrap(<div className="text-center p-4"><p>{t('dr_err_generic')}</p></div>);
  if (!info) return wrap(<div className="text-center p-4 text-muted">{t('dr_loading')}</div>);

  const refunded = info.state === 'refunded';
  const canceled = info.state === 'canceled';
  const showForm = !refunded && !canceled && (editing || !info.bank);
  const lastNight = info.end_date;
  const checkout = lastNight ? moment(lastNight).add(1, 'day') : null;

  return wrap(
    <>
      <h4 className="mb-1">{info.hotel_name}</h4>
      <div className="text-muted mb-3">{t('security_deposit')}</div>

      <div className="border rounded p-3 mb-3">
        <div className="d-flex"><span className="flex-grow-1 text-muted">{t('booking_no')}</span><b>{info.booking_no}</b></div>
        {info.room_name && <div className="d-flex mt-1"><span className="flex-grow-1 text-muted">{t('room')}</span><b className="text-end">{info.room_name}</b></div>}
        {info.start_date && <div className="d-flex mt-1"><span className="flex-grow-1 text-muted">{t('stay_date')}</span><b className="text-end">{fmtDate(info.start_date)}{checkout ? ` - ${fmtDate(checkout)}` : ''}</b></div>}
        <div className="d-flex mt-1"><span className="flex-grow-1 text-muted">{t('security_deposit')}</span><b>{baht(info.deposit_amount)}</b></div>
      </div>

      {/* สถานะ */}
      {refunded && (
        <div className="border border-success rounded p-3 mb-3" style={{ background: '#F1F7F3' }}>
          <div className="fw-bold text-success" style={{ fontSize: 18 }}>
            {t('dr_refunded_line', { amount: baht(info.refund_amount), date: info.refunded_time ? moment(info.refunded_time).format('D MMM YYYY HH:mm') : '-' })}
          </div>
          {(info.expenses || []).length > 0 && (
            <div className="mt-2">
              <div className="text-muted">{t('deposit_damage_deducted')}</div>
              {info.expenses.map((x, i) => (
                <div key={i} className="d-flex"><span className="flex-grow-1">{x.name} × {numberFormat(x.qty, true)}</span><span>-{baht(x.total_price)}</span></div>
              ))}
            </div>
          )}
          {info.refunded_slip && <a href={info.refunded_slip} target="_blank" rel="noreferrer"><img src={info.refunded_slip} alt="slip" className="mt-3 rounded" style={{ maxWidth: '100%', maxHeight: 360 }} /></a>}
        </div>
      )}
      {canceled && <div className="alert alert-secondary">{t('dr_canceled')}</div>}

      {saved && !showForm && <div className="alert alert-success">{t('dr_saved')}</div>}

      {!refunded && !canceled && info.bank && !editing && (
        <div className="border rounded p-3 mb-3">
          <div className="text-muted mb-1">{t('dr_refund_to')}</div>
          <div className="fw-bold">{info.bank.bank_name}</div>
          <div style={{ fontSize: 18, letterSpacing: 1 }}>{info.bank.account_no}</div>
          <div>{info.bank.account_name}</div>
          <div className="mt-2 fw-bold text-warning">{info.inspected ? t('dr_inspected_processing') : t('dr_waiting_refund')}</div>
          <button type="button" className="btn btn-outline-dark btn-sm mt-3" onClick={startEdit}>{t('dr_edit_bank')}</button>
        </div>
      )}

      {showForm && (
        <form className="border rounded p-3 mb-3" onSubmit={submit}>
          <div className="fw-bold mb-1">{t('dr_form_title')}</div>
          <div className="text-muted small mb-3">{t('dr_form_hint')}</div>

          <label className="form-label mb-1">{t('dr_bank')}</label>
          <select className="form-select mb-2" value={bank} onChange={(e) => setBank(e.target.value)}>
            <option value="">{t('dr_select_bank')}</option>
            {BANKS.map((b) => <option key={b} value={b}>{b}</option>)}
            <option value={OTHER}>{t('dr_other_bank')}</option>
          </select>
          {bank === OTHER && <input className="form-control mb-2" value={bankOther} onChange={(e) => setBankOther(e.target.value)} placeholder={t('dr_bank_name_placeholder')} />}

          <label className="form-label mb-1 mt-1">{t('dr_account_no')}</label>
          <input className="form-control mb-2" type="text" inputMode="numeric" autoComplete="off" value={accountNo}
            onChange={(e) => setAccountNo(e.target.value.replace(/[^0-9-\s]/g, ''))} placeholder="xxxxxxxxxx" />

          <label className="form-label mb-1 mt-1">{t('dr_account_name')}</label>
          <input className="form-control mb-3" value={accountName} onChange={(e) => setAccountName(e.target.value)} />

          {formError && <div className="alert alert-danger py-2">{formError}</div>}
          <button type="submit" className="btn btn-dark w-100 py-2" disabled={busy}>{busy ? t('dr_saving') : t('dr_submit')}</button>
          {editing && info.bank && <button type="button" className="btn btn-link w-100 mt-1 text-muted" onClick={() => setEditing(false)}>{t('cancel')}</button>}
        </form>
      )}

      {info.hotel_tel && <div className="text-muted small text-center mt-3">{t('dr_contact_hotel')} <a href={`tel:${info.hotel_tel}`}>{info.hotel_tel}</a></div>}
    </>,
  );
}
